import { useEffect, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  MinusIcon,
  PlusIcon,
  ArrowsPointingOutIcon,
  PencilIcon,
  CheckIcon,
  SquaresPlusIcon,
  DocumentTextIcon,
  CubeIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import {
  buildDrawingViewerTitleMeta,
  formatDrawingLocationMeta,
} from "./drawingStructureApi";

// Treat as "Andre" when there is no actual floor assignment id, regardless of legacy strings.
const isAndreDrawing = (item) =>
  !(item?.drawing_floor?.id || item?.drawing_floor_id);

const groupSiblingDrawings = (items) => {
  const floorItems = [];
  const andreItems = [];
  (items || []).forEach((item) => {
    if (isAndreDrawing(item)) andreItems.push(item);
    else floorItems.push(item);
  });
  return { floorItems, andreItems };
};

const SiblingDrawingOption = ({ t, drawing, item, navigate, setOpen }) => {
  const active = String(item.id) === String(drawing.id);
  const meta = formatDrawingLocationMeta(t, item, { includeProject: true });
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={active}
        onClick={() => {
          setOpen(false);
          if (!active) navigate(`/dashboard/drawings/${item.id}`);
        }}
        className={`flex w-full items-center gap-2.5 border-b border-[#e5e8ec] px-3 py-2.5 text-left last:border-b-0 ${
          active ? "bg-[#eaf1fe]" : "hover:bg-[#f6f7f9]"
        }`}
      >
        <DocumentTextIcon
          className={`h-4 w-4 flex-shrink-0 ${
            active ? "text-[#2b6ef0]" : "text-[#969da8]"
          }`}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-[#14171c]">
            {item.title || item.displayTitle || `#${item.id}`}
          </span>
          {meta && (
            <span className="mt-0.5 block truncate font-mono text-[11px] text-[#59616d]">
              {meta}
            </span>
          )}
        </span>
        {active && (
          <CheckIcon className="h-4 w-4 flex-shrink-0 text-[#2b6ef0]" />
        )}
      </button>
    </li>
  );
};

const IconBtn = ({ onClick, disabled, title, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`grid h-[26px] w-[26px] place-items-center rounded-[6px] border-none bg-transparent text-[#59616d] transition-colors hover:bg-[#eef0f3] hover:text-[#14171c] disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent disabled:hover:text-[#59616d] ${className}`}
  >
    {children}
  </button>
);

const DesktopDrawingSwitcher = ({
  t,
  drawing,
  isVersionMode,
  activeVersion,
  versions = [],
  siblingDrawings = [],
  siblingDrawingsLoading = false,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const { locationLine, revisionLine } = buildDrawingViewerTitleMeta({
    t,
    drawing,
    isVersionMode,
    activeVersion,
    versions,
  });
  const hasProject = !!drawing.project?.id;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const emptyMessage = hasProject
    ? t("drawings.viewer.mobile.no_sibling_drawings")
    : t("drawings.viewer.mobile.no_project_drawings");

  return (
    <div ref={rootRef} className="relative min-w-0 max-w-[min(420px,42vw)]">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex min-w-0 max-w-full items-center gap-1.5 rounded-[10px] border border-transparent px-2 py-1 text-left transition-colors hover:border-[#e5e8ec] hover:bg-[#f6f7f9]"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("drawings.viewer.mobile.switch_drawing")}
        title={t("drawings.viewer.mobile.switch_drawing")}
      >
        <div className="min-w-0 flex-1 leading-[1.15]">
          <div className="flex items-center gap-1.5 text-[13.5px] font-semibold tracking-[-0.01em] text-[#14171c]">
            <span className="truncate">{drawing.title}</span>
            {isVersionMode && activeVersion && (
              <span className="flex-shrink-0 rounded-full bg-[#eaf1fe] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#1d4fb0]">
                {activeVersion.title}
              </span>
            )}
          </div>
          {(locationLine || revisionLine) && (
            <div className="font-mono text-[11px] tracking-[-0.01em] text-[#59616d]">
              {locationLine ? (
                <div className="break-words">{locationLine}</div>
              ) : null}
              {revisionLine ? (
                <div className="whitespace-nowrap">{revisionLine}</div>
              ) : null}
            </div>
          )}
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-[#59616d] transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-[calc(100%+6px)] z-50 w-[min(360px,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-[12px] border border-[#e5e8ec] bg-white shadow-[0_16px_40px_rgba(20,23,28,0.16),0_2px_8px_rgba(20,23,28,0.08)]"
        >
          <div className="border-b border-[#e5e8ec] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.04em] text-[#969da8]">
            {t("drawings.viewer.mobile.switch_drawing")}
          </div>
          <div className="max-h-[min(360px,50vh)] overflow-y-auto">
            {siblingDrawingsLoading ? (
              <p className="px-3 py-6 text-center text-[13px] text-[#59616d]">
                {t("hr.common.loading")}
              </p>
            ) : siblingDrawings.length === 0 ? (
              <div className="px-3 py-5 text-center">
                <p className="m-0 text-[13px] leading-relaxed text-[#59616d]">
                  {emptyMessage}
                </p>
                <Link
                  to="/dashboard/drawings"
                  className="mt-3 inline-flex text-[13px] font-semibold text-[#2b6ef0]"
                  onClick={() => setOpen(false)}
                >
                  {t("drawings.viewer.mobile.to_drawings_list")}
                </Link>
              </div>
            ) : (
              <>
                {!hasProject && (
                  <div className="border-b border-[#e5e8ec] px-3 py-2.5">
                    <p className="m-0 text-[12px] leading-relaxed text-[#59616d]">
                      {t("drawings.viewer.mobile.no_project_switch_hint")}
                    </p>
                  </div>
                )}
                {(() => {
                  const { floorItems, andreItems } =
                    groupSiblingDrawings(siblingDrawings);
                  return (
                    <>
                      {floorItems.length > 0 && (
                        <ul className="m-0 list-none p-0">
                          {floorItems.map((item) => (
                            <SiblingDrawingOption
                              key={item.id}
                              t={t}
                              drawing={drawing}
                              item={item}
                              navigate={navigate}
                              setOpen={setOpen}
                            />
                          ))}
                        </ul>
                      )}
                      {andreItems.length > 0 && (
                        <>
                          <div className="border-y border-[#e5e8ec] bg-[#f6f7f9] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.04em] text-[#969da8]">
                            {t("drawings.structure.andre")}
                          </div>
                          <ul className="m-0 list-none p-0">
                            {andreItems.map((item) => (
                              <SiblingDrawingOption
                                key={item.id}
                                t={t}
                                drawing={drawing}
                                item={item}
                                navigate={navigate}
                                setOpen={setOpen}
                              />
                            ))}
                          </ul>
                        </>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const DrawingViewerTopBar = ({
  t,
  drawing,
  page,
  pageCount,
  onPageChange,
  zoom,
  onZoomChange,
  onFit,
  pendingCount,
  saving,
  onSaveChanges,
  onEdit,
  onTogglePanel,
  isVersionMode,
  activeVersion,
  versions = [],
  onCreateVersion,
  onSwitchVersion,
  creatingVersion,
  onAdd,
  placingObservation,
  siblingDrawings = [],
  siblingDrawingsLoading = false,
  canEdit = true,
  onOpen3d,
  onAlign3d,
  aligning3d,
}) => {
  const saveLabel =
    pendingCount > 0
      ? `${t("drawings.save_changes")} (${pendingCount})`
      : t("common.edit");

  return (
    <header className="z-30 hidden min-h-14 flex-shrink-0 items-center gap-3.5 border-b border-[#e5e8ec] bg-white px-3.5 py-1 xl:flex">
      <div className="flex min-w-0 items-center gap-2.5 pr-1.5">
        <Link
          to="/dashboard/drawings"
          className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-[7px] bg-gradient-to-br from-[#2b6ef0] to-[#1d4fb0] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_1px_2px_rgba(20,23,28,0.06)]"
          title={t("common.back")}
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Link>
        {drawing?.project?.id || drawing?.project_id ? (
          <Link
            to={`/dashboard/projects/${drawing.project?.id || drawing.project_id}`}
            className="shrink-0 rounded-[7px] border border-[#C9D6FA] bg-[#F3F6FF] px-2.5 py-1 text-[12px] font-medium text-[#1F4BE0] hover:bg-[#E6ECFF]"
          >
            {t("drawings.list_ui.go_to_project")}
          </Link>
        ) : null}
        <DesktopDrawingSwitcher
          t={t}
          drawing={drawing}
          isVersionMode={isVersionMode}
          activeVersion={activeVersion}
          versions={versions}
          siblingDrawings={siblingDrawings}
          siblingDrawingsLoading={siblingDrawingsLoading}
        />
      </div>

      <div className="flex-1" />

      {pageCount > 1 && (
        <div className="flex items-center gap-0.5 rounded-[9px] border border-[#e5e8ec] bg-[#f6f7f9] p-[3px]">
          <IconBtn
            disabled={page <= 0}
            onClick={() => onPageChange(page - 1)}
            title={t("drawings.viewer.prev_page")}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </IconBtn>
          <div className="min-w-[78px] select-none px-2 text-center font-mono text-xs text-[#14171c]">
            <b>{page + 1}</b>
            <span className="text-[#969da8]"> / {pageCount}</span>
          </div>
          <IconBtn
            disabled={page >= pageCount - 1}
            onClick={() => onPageChange(page + 1)}
            title={t("drawings.viewer.next_page")}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </IconBtn>
        </div>
      )}

      <div className="flex items-center gap-0.5 rounded-[9px] border border-[#e5e8ec] bg-[#f6f7f9] p-[3px]">
        <IconBtn onClick={() => onZoomChange(zoom * 0.8)} title={t("drawings.viewer.zoom_out")}>
          <MinusIcon className="h-4 w-4" />
        </IconBtn>
        <div className="min-w-[50px] select-none px-1.5 text-center font-mono text-xs text-[#14171c]">
          {Math.round(zoom * 100)}%
        </div>
        <IconBtn onClick={() => onZoomChange(zoom * 1.25)} title={t("drawings.viewer.zoom_in")}>
          <PlusIcon className="h-4 w-4" />
        </IconBtn>
        <IconBtn onClick={onFit} title={t("drawings.viewer.fit_to_screen")}>
          <ArrowsPointingOutIcon className="h-4 w-4" />
        </IconBtn>
      </div>

      <div className="hidden items-center gap-1 lg:flex">
        <button
          type="button"
          onClick={() => onSwitchVersion(null)}
          className={`h-[34px] rounded-[9px] border px-2.5 text-[12px] font-semibold ${
            !isVersionMode
              ? "border-[#2b6ef0] bg-[#eaf1fe] text-[#1d4fb0]"
              : "border-[#e5e8ec] bg-white text-[#14171c] hover:bg-[#f6f7f9]"
          }`}
        >
          {t("drawings.viewer.live_drawing")}
        </button>
        {canEdit && (
          <button
            type="button"
            disabled={creatingVersion}
            onClick={onCreateVersion}
            className="h-[34px] rounded-[9px] border border-[#e5e8ec] bg-white px-2.5 text-[12px] font-semibold text-[#14171c] hover:bg-[#f6f7f9] disabled:opacity-50"
          >
            {t("drawings.viewer.create_version")}
          </button>
        )}
      </div>

      {onOpen3d && drawing.project?.id && (
        <button
          type="button"
          onClick={onOpen3d}
          title={t("drawings.viewer.see_3d")}
          className="inline-flex h-[34px] items-center gap-1.5 rounded-[9px] border border-[#e5e8ec] bg-white px-3 text-[13px] font-semibold text-[#14171c] hover:bg-[#f6f7f9]"
        >
          <CubeIcon className="h-4 w-4" />
          {t("drawings.viewer.see_3d")}
        </button>
      )}

      {onAlign3d && drawing.project?.id && (
        <button
          type="button"
          onClick={onAlign3d}
          disabled={aligning3d}
          title={t("drawings.viewer.align_3d")}
          className={`inline-flex h-[34px] items-center gap-1.5 rounded-[9px] border px-3 text-[13px] font-semibold disabled:opacity-60 ${
            aligning3d
              ? "border-[#2b6ef0] bg-[#eaf1fe] text-[#1d4fb0]"
              : "border-[#e5e8ec] bg-white text-[#14171c] hover:bg-[#f6f7f9]"
          }`}
        >
          <ViewfinderCircleIcon className="h-4 w-4" />
          {t("drawings.viewer.align_3d")}
        </button>
      )}

      {canEdit && (
        <button
          type="button"
          onClick={onAdd}
          disabled={placingObservation}
          className="inline-flex h-[34px] items-center gap-1.5 rounded-[9px] border border-[#2b6ef0] bg-[#2b6ef0] px-3 text-[13px] font-semibold text-white shadow-[0_1px_2px_rgba(43,110,240,0.35)] hover:bg-[#2462db] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <PlusIcon className="h-4 w-4" />
          {t("drawings.observation.add")}
        </button>
      )}

      <button
        type="button"
        onClick={onTogglePanel}
        className="hidden h-[34px] items-center gap-1.5 rounded-[9px] border border-[#e5e8ec] bg-white px-3 text-[13px] font-semibold text-[#14171c] hover:bg-[#f6f7f9] md:inline-flex"
      >
        <SquaresPlusIcon className="h-4 w-4" />
        {t("drawings.viewer.modules")}
      </button>

      {canEdit && (
        <button
          type="button"
          onClick={pendingCount > 0 ? onSaveChanges : onEdit}
          disabled={saving}
          className={`inline-flex h-[34px] items-center gap-1.5 rounded-[9px] px-3 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
            pendingCount > 0
              ? "border border-[#2b6ef0] bg-[#2b6ef0] text-white shadow-[0_1px_2px_rgba(43,110,240,0.35)] hover:bg-[#2462db]"
              : "border border-[#e5e8ec] bg-white text-[#14171c] hover:bg-[#f6f7f9]"
          }`}
        >
          {pendingCount > 0 ? (
            <CheckIcon className="h-4 w-4" />
          ) : (
            <PencilIcon className="h-4 w-4" />
          )}
          {saving ? t("common.saving") : saveLabel}
        </button>
      )}
    </header>
  );
};
